import React, { useState } from 'react';
import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import GoogleButton from './GoogleButton';
import {
  PRICING, PRODUCT_CONFIG, productConfig,
  checkoutFor, trialFor, fetchMyLicenceUnlock,
  googleSignInResult, googleErrorMessage, entitles, PLANNER_DARK,
  goToPostSignInDestination,
} from './licenceApi';

/* Product-aware pricing. On a fresh purchase it surfaces ALL THREE options —
   this tool, the other tool, and the bundle — via a segmented selector; the
   route's own product is selected by default. Renewals (renewalKey set) are
   single-product and skip the selector. Checkout is web-only via Stripe. */
export default function PricingPlans({
  product = 'roadmap', renewalKey = '', defaultEmail = '', fromTrial = false,
  signedIn = false, onUnlock, onBack,
}) {
  const routeProduct = product === 'planner' ? 'planner' : 'roadmap';
  const otherProduct = routeProduct === 'roadmap' ? 'planner' : 'roadmap';
  const isRenew = !!renewalKey;
  // A trial upgrade reuses the trial's licence key (so progress carries over) but
  // should read as a first purchase, not a renewal.
  const showRenewCopy = isRenew && !fromTrial;

  // Which product the buyer is purchasing. Renewals lock to the licence's own
  // scope; new purchases start on the route's product and can switch. While the
  // planner is dark it is not for sale, so a fresh purchase always starts (and
  // stays) on the roadmap — the planner/bundle options are removed below.
  const [selected, setSelected] = useState(isRenew ? product : (PLANNER_DARK ? 'roadmap' : routeProduct));
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy]   = useState('');
  const [err, setErr]     = useState('');            // buy-plan errors (rendered under the plan cards)
  const [trialErr, setTrialErr] = useState('');      // trial errors (rendered next to the trial button)
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialDone, setTrialDone] = useState(null); // null | { product, emailed, key }
  const [signedInEmail, setSignedInEmail] = useState(signedIn ? defaultEmail : '');
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleErr, setGoogleErr]   = useState('');

  const selCfg = selected === 'bundle'
    ? { noun: 'Bundle' }
    : productConfig(selected);
  // Only the roadmap table survives — it IS the Narratoria plan. The fallback
  // keeps a stale `selected` (e.g. a bookmarked ?product=planner) rendering.
  const pricing = PRICING[selected] || PRICING.roadmap;

  // ONE PLAN (Julie, 2026-07-29). There is nothing to switch between any more:
  // the single Narratoria plan includes all three tools, so the planner and
  // bundle options are gone unconditionally rather than hidden behind a flag
  // waiting to be flipped. Their PRICING tables have been deleted outright.
  // A single entry means the selector renders as a plain label, not a chooser.
  const SWITCH = [{ id: 'roadmap', label: PRICING.roadmap.label }];

  const continueWithGoogle = async () => {
    setGoogleErr(''); setErr(''); setGoogleBusy(true);
    try {
      const r = await googleSignInResult(routeProduct);
      // An ENTITLED account goes straight to the hub on sign-in. An unentitled
      // one stays here so they can pick a plan or start a trial — bouncing them
      // to a hub of locked cards would lose the checkout they came for.
      if (r.unlocked && r.user) { await goToPostSignInDestination(r.user); return; }
      if (r.unlocked) { if (onUnlock) onUnlock(r.data, r.expired); return; }
      setSignedInEmail(r.email); setEmail(r.email);
    } catch (e) { setGoogleErr(googleErrorMessage(e)); }
    finally { setGoogleBusy(false); }
  };

  const buy = async (term) => {
    setErr('');
    if (!isRenew && !email.trim()) { setErr('Enter the email your licence should be tied to.'); return; }
    setBusy(term);
    try {
      const res = await checkoutFor(isRenew ? product : selected)({
        term,
        email:  isRenew ? undefined : email.trim().toLowerCase(),
        key:    isRenew ? renewalKey : undefined,
        origin: window.location.origin,
        returnPath: productConfig(routeProduct).homePath,
      });
      if (res.data?.url) window.location.assign(res.data.url);
      else { setErr('Could not start checkout. Please try again.'); setBusy(''); }
    } catch (e) { setErr(e?.message || 'Could not start checkout. Please try again.'); setBusy(''); }
  };

  const startTrial = async () => {
    setTrialErr('');
    // The trial callable now takes the email from the VERIFIED auth token and
    // ignores anything sent in the payload, so a typed address can no longer
    // start a trial. Sign-in is therefore a precondition, not a convenience.
    if (!signedInEmail) {
      setTrialErr('Sign in with Google first — your free trial is tied to your account.');
      return;
    }
    setTrialBusy(true);
    try {
      const res = await trialFor(selected)({});
      if (!res.data?.ok) { setTrialErr('Could not start your trial. Please try again.'); setTrialBusy(false); return; }
      // If the trial entitles the CURRENT route and they're signed in, unlock in
      // place (getMyLicence auto-claims their verified email). Otherwise show the
      // "check your email" panel pointing at the right tool.
      if (signedInEmail && onUnlock && entitles(selected, routeProduct)) {
        let unlock = await fetchMyLicenceUnlock(routeProduct);
        if (!unlock) { await new Promise(r => setTimeout(r, 900)); unlock = await fetchMyLicenceUnlock(routeProduct); }
        if (unlock) { onUnlock(unlock.data, unlock.expired); return; }
      }
      // Carry the email outcome + key so the success panel tells the truth: show
      // "check your inbox" only if the email actually went out, otherwise show the
      // key inline. (emailed === undefined from an older backend → assume sent.)
      setTrialDone({ product: selected, emailed: res.data?.emailed !== false, key: res.data?.key });
    } catch (e) { setTrialErr(e?.message || 'Could not start your trial. Please try again.'); }
    finally { setTrialBusy(false); }
  };

  if (trialDone) {
    const tp = trialDone.product;
    const goHome = tp === 'bundle' ? PRODUCT_CONFIG.planner.homePath : productConfig(tp).homePath;
    const sameRoute = entitles(tp, routeProduct);
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-ink-900 via-ink to-ink-900" style={{fontFamily:"'Cormorant Garamond',Georgia,serif"}}>
        <div className="relative w-full max-w-md text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-green-500/40 bg-green-500/15">
            <Check className="h-8 w-8 text-green-400" />
          </div>
          {trialDone.emailed ? (
            <>
              <h1 className="font-cinzel text-2xl font-bold text-white">Your free trial is on its way</h1>
              <p className="mt-3 text-sm leading-relaxed text-parchment/80">We've emailed your licence key to <span className="text-gold">{(signedInEmail || email).trim().toLowerCase()}</span>. Check your inbox (and spam), then enter the key to unlock your 10-day trial.</p>
            </>
          ) : (
            <>
              <h1 className="font-cinzel text-2xl font-bold text-white">Your free trial is ready</h1>
              <p className="mt-3 text-sm leading-relaxed text-parchment/80">We couldn't send your email just now, so here is your licence key — copy it somewhere safe, then enter it to unlock your 10-day trial:</p>
              {trialDone.key && <p className="mt-3 select-all rounded-xl border border-gold/40 bg-white/5 px-4 py-3 font-mono text-base font-bold tracking-wider text-gold">{trialDone.key}</p>}
            </>
          )}
          {sameRoute ? (
            <Button onClick={onBack} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold to-gold-deep px-5 py-3 text-sm font-bold text-ink hover:from-gold-light hover:to-gold">
              Enter my key <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <a href={goHome} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold to-gold-deep px-5 py-3 text-sm font-bold text-ink hover:from-gold-light hover:to-gold">
              Open it <ArrowRight className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-ink-900 via-ink to-ink-900" style={{fontFamily:"'Cormorant Garamond',Georgia,serif"}}>
      <div className="relative w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h1 className="font-cinzel text-3xl font-bold text-white">{showRenewCopy ? 'Renew your' : 'Choose your'} <span className="text-gold">{isRenew ? selCfg.noun : 'Narratoria'}</span> plan</h1>
          <p className="mt-2 text-sm text-parchment/50">One-off payment — no auto-renewal. Your saved progress is always kept.</p>
        </div>

        {/* Product selector — surfaces roadmap / planner / bundle (new purchase only).
            Hidden when only one product is on offer (e.g. planner dark → roadmap only). */}
        {!isRenew && SWITCH.length > 1 && (
          <div className="mx-auto mb-6 flex max-w-lg overflow-hidden rounded-full border-2 border-gold/60 bg-gold/5">
            {SWITCH.map(s => (
              <button key={s.id} type="button" onClick={() => { setSelected(s.id); setErr(''); }}
                className={`flex-1 whitespace-nowrap px-3 py-2 font-cinzel text-xs font-bold transition-all sm:text-sm ${selected===s.id ? 'bg-gold text-ink' : 'text-parchment/70 hover:bg-gold/15'}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}
        {!isRenew && <p className="mx-auto mb-6 max-w-md text-center text-xs italic text-parchment/50">{pricing.blurb}</p>}

        {!isRenew && (
          <div className="mx-auto mb-6 max-w-md">
            {!signedInEmail && (
              <>
                <GoogleButton onClick={continueWithGoogle} busy={googleBusy} />
                <p className="mt-2 text-center text-xs text-parchment/40">Start in one tap — your trial is claimed to your account automatically.</p>
                {googleErr && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/10 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" /><p className="text-xs text-rose-200">{googleErr}</p>
                  </div>
                )}
                <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-parchment/40">
                  <span className="h-px flex-1 bg-white/10" /> or use your email <span className="h-px flex-1 bg-white/10" />
                </div>
              </>
            )}

            {signedInEmail ? (
              <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-parchment/50">Signed in as</p>
                <p className="mt-0.5 truncate text-sm font-semibold text-white">{signedInEmail}</p>
              </div>
            ) : (
              <>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-parchment/50">Your email (your licence is tied to this)</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                  className="w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm text-white placeholder-parchment/40 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
                  style={{userSelect:'text',WebkitUserSelect:'text'}} />
              </>
            )}

            {/* Free 10-day trial — no card required (uses the email/account above) */}
            <div className="mt-4 rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-center">
              <p className="text-sm font-bold text-green-200">Try free for 10 days — no card required</p>
              {/* Was "Start with the full {label}" — the label is now a phrase
                  ("the full toolkit"), so the sentence is written out instead of
                  interpolated, which would read "the full the full toolkit". */}
              <p className="mt-1 text-xs text-parchment/70">Every tool included from day one. Your progress is saved and carries straight over if you buy.</p>
              <Button onClick={startTrial} disabled={trialBusy || !!busy}
                className="mt-3 inline-flex items-center gap-2 rounded-xl border border-green-400/50 bg-green-500/20 px-4 py-2.5 text-sm font-bold text-green-100 hover:bg-green-500/30 disabled:opacity-50">
                {trialBusy ? 'Starting…' : 'Start my free trial'}
              </Button>
              {trialErr && <p className="mt-3 text-xs font-semibold text-red-300">{trialErr}</p>}
            </div>

            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest text-parchment/50">
              <span className="h-px flex-1 bg-white/10" /> or choose a plan <span className="h-px flex-1 bg-white/10" />
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          {pricing.plans.map(p => (
            <div key={p.term} className={`relative overflow-hidden rounded-2xl border p-5 text-center ${p.featured ? 'border-gold/60 bg-gold/10' : 'border-white/10 bg-white/5'}`}>
              {p.featured && <div className="mb-2 text-xs font-bold uppercase tracking-widest text-gold">{p.note}</div>}
              <p className="text-lg font-bold text-white">{p.qty}</p>
              <p className="mt-1 text-3xl font-bold text-gold">{p.price}</p>
              {!p.featured && <p className="mt-1 text-xs text-parchment/50">{p.note}</p>}
              <Button onClick={() => buy(p.term)} disabled={!!busy}
                className="mt-4 w-full rounded-xl bg-gradient-to-r from-gold to-gold-deep px-4 py-2.5 text-sm font-bold text-ink hover:from-gold-light hover:to-gold disabled:opacity-50">
                {busy === p.term ? 'Starting…' : (showRenewCopy ? 'Renew' : 'Buy')}
              </Button>
            </div>
          ))}
        </div>

        {err && <p className="mt-4 text-center text-xs text-red-300">{err}</p>}
        <p className="mt-6 text-center text-xs text-parchment/40">Secure checkout by Stripe. You'll be emailed your licence key after payment.</p>
        {onBack && <div className="mt-4 text-center"><button type="button" onClick={onBack} className="text-xs text-parchment/50 underline hover:text-gold">← Back</button></div>}
      </div>
    </div>
  );
}
